/**
 * Thrown when the admin backend refuses a solution-version file upload.
 *
 * It carries the status only. The refusal body is another service's text (on
 * the candidate it was an AWS IAM message naming the backend's user ARN, the
 * account id and the bucket path), so it is deliberately not copied into the
 * message: whatever catches this error may show `message` to the user.
 */
export class SolutionFilesUploadError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`solution file upload was refused with status ${status}`);
    this.name = 'SolutionFilesUploadError';
    this.status = status;
  }
}

/**
 * Posts files to the admin backend for one solution version.
 *
 * Resolves when the backend accepts them, rejects with a
 * `SolutionFilesUploadError` when it refuses, and lets a network failure
 * through as the original error. It never shows anything: the caller owns the
 * one message the user sees.
 */
export const postSolutionVersionFiles = async (
  solutionVersionId: string,
  files: File[]
): Promise<void> => {
  const uploadUrl = `${import.meta.env.FE_ADMIN_URL}/rest/solution-version-upload-files/${solutionVersionId}`;
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await fetch(uploadUrl, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new SolutionFilesUploadError(response.status);
  }
};
